import React, { use, useEffect, useState } from "react";
import { Button, Card, Container, Form, Table } from "react-bootstrap";
import { RootState } from "../../store/store";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
	fetchSeasonsDataThunk,
	fetchSeriesThunk,
	fetchSportsThunk,
	getEventsThunk,
} from "../../store/thunks/fantasyThunk";
import {
	BestCountry,
	Event,
	initialEvent,
	initialSeason,
	initialSeries,
	initialSport,
	MostPickedRiderCount,
	RiderValueRatio,
	Season,
	Series,
	Sport,
} from "../../types/model";
import TeamResultsTable from "../Results/Components/TeamResultsTable";
import { getMostPickedRider } from "../../services/snapshotService";
import getCountryISO2 from "country-iso-3-to-2";
import ReactCountryFlag from "react-country-flag";
import {
	getBestCountryForEvent,
	getResultMVP,
} from "../../services/resultService";
import AdminLayout from "./AdminLayout";

const AdminAnalyticsPage: React.FC = () => {
	const { events, seasons, sports, series } = useAppSelector(
		(state: RootState) => state.fantasy,
	);

	const dispatch = useAppDispatch();

	const [selectedEvent, setSelectedEvent] = useState<Event>(initialEvent);
	const [selectedSeason, setSelectedSeason] =
		React.useState<Season>(initialSeason);
	const [selectedSport, setSelectedSport] = useState<Sport>(initialSport);
	const [selectedSeries, setSelectedSeries] =
		React.useState<Series>(initialSeries);

	const [mostPickedRiders, setMostPickedRiders] = useState<
		MostPickedRiderCount[]
	>([]);

	const [mvps, setMVP] = useState<RiderValueRatio[]>([]); // Adjust the type as per your data structure

	const [bestCountry, setBestCountry] = useState<BestCountry[]>([]);

	const filteredEvents = events.filter(
		(event) =>
			event.sport._id === selectedSport._id &&
			event.season === selectedSeason._id &&
			event.series._id === selectedSeries._id,
	);

	useEffect(() => {
		fetchEvents();
		fetchSeasons();
		fetchSports();
		fetchSeries();
	}, []);

	useEffect(() => {
		if (selectedEvent._id) {
			fetchMostPickedRiders();
			fetchBestCountry();
			fetchMVP();
		}
	}, [selectedEvent]);

	const fetchEvents = async () => {
		try {
			dispatch(getEventsThunk());
		} catch (error) {
			console.error("Error fetching events:", error);
		}
	};

	const fetchSeasons = async () => {
		try {
			dispatch(fetchSeasonsDataThunk());
		} catch (error) {
			console.error("Error fetching seasons:", error);
		}
	};

	const fetchSports = async () => {
		try {
			dispatch(fetchSportsThunk());
		} catch (error) {
			console.error("Error fetching sports:", error);
		}
	};

	const fetchSeries = async () => {
		try {
			dispatch(fetchSeriesThunk());
		} catch (error) {
			console.error("Error fetching series:", error);
		}
	};

	const fetchMostPickedRiders = async () => {
		try {
			// Fetch most picked riders for the selected event
			const res = await getMostPickedRider(selectedEvent._id);
			if (res) {
				// Process the response as needed
				console.log("Most picked riders:", res.data);
				const sorted = res.data.sort(
					(a: MostPickedRiderCount, b: MostPickedRiderCount) =>
						b.count - a.count,
				);
				setMostPickedRiders(sorted);
			}
		} catch (error) {
			console.error("Error fetching most picked riders:", error);
		}
	};

	const fetchBestCountry = async () => {
		try {
			const res = await getBestCountryForEvent(selectedEvent._id);
			if (res) {
				// Process the response as needed
				console.log("Best country for event:", res.data);
				setBestCountry(res.data);
			}
		} catch (error) {
			console.error("Error fetching best country for event:", error);
		}
	};

	const fetchMVP = async () => {
		try {
			const res = await getResultMVP(selectedEvent._id);
			if (res) {
				setMVP(res.data);
			}
		} catch (error) {
			console.error("Error fetching MVP for event:", error);
		}
	};

	return (
		<AdminLayout>
			<Container className="text-white">
			<h1 className="my-3 fw-bold">Analytics</h1>
			<Card className="my-3 shadow bg-secondary text-light border-0">
				<Card.Body>
					<div className="d-flex gap-3 flex-wrap">
						<div>
							<label htmlFor="sport-select" className="form-label">
								Sport
							</label>
							{/* React Bootstrap Form.Select */}
							<Form.Select
								id="sport-select"
								value={selectedSport._id}
								onChange={(e) => {
									const sport =
										sports.find((s) => s._id === e.target.value) ||
										initialSport;
									setSelectedSport(sport);
								}}
							>
								<option value="">Select Sport</option>
								{sports.map((sport) => (
									<option key={sport._id} value={sport._id}>
										{sport.name}
									</option>
								))}
							</Form.Select>
						</div>
						<div>
							<label htmlFor="series-select" className="form-label">
								Series
							</label>
							<Form.Select
								id="series-select"
								value={selectedSeries._id}
								onChange={(e) => {
									const seriesObj =
										series.find((s) => s._id === e.target.value) ||
										initialSeries;
									setSelectedSeries(seriesObj);
								}}
							>
								<option value="">Select Series</option>
								{series.map((seriesObj) => (
									<option key={seriesObj._id} value={seriesObj._id}>
										{seriesObj.name}
									</option>
								))}
							</Form.Select>
						</div>
						<div>
							<label htmlFor="season-select" className="form-label">
								Season
							</label>
							<Form.Select
								id="season-select"
								value={selectedSeason._id}
								onChange={(e) => {
									const season =
										seasons.find((s) => s._id === e.target.value) ||
										initialSeason;
									setSelectedSeason(season);
								}}
							>
								<option value="">Select Season</option>
								{seasons.map((season) => (
									<option key={season._id} value={season._id}>
										{season.name}
									</option>
								))}
							</Form.Select>
						</div>
						<div>
							<label htmlFor="event-select" className="form-label">
								Event
							</label>
							<Form.Select
								id="event-select"
								value={selectedEvent._id}
								onChange={(e) => {
									const event =
										events.find(
											(event: Event) => event._id === e.target.value,
										) || initialEvent;
									setSelectedEvent(event);
								}}
							>
								<option value="">Select Event</option>
								{filteredEvents.map((event) => (
									<option key={event._id} value={event._id}>
										{event.name}
									</option>
								))}
							</Form.Select>
						</div>
					</div>
				</Card.Body>
			</Card>
			{/* Add analytics content here */}
			<TeamResultsTable event={selectedEvent} />
			<Card className="my-3 shadow bg-secondary text-light border-0 mt-5">
				<Card.Body style={{ maxHeight: "400px", overflowY: "auto" }}>
					<h4 className="fw-bold ms-2 d-flex justify-content-between">
						<div className="">MOST PICKED RIDERS</div>
						<div className="">
							{selectedEvent && selectedEvent.location
								? selectedEvent.location
								: "N/A"}
							{selectedEvent && selectedEvent.country && (
								<ReactCountryFlag
									countryCode={selectedEvent.country}
									svg
									className="mb-1 ms-2"
									style={{ width: "1.5em", height: "1.5em" }}
								/>
							)}
						</div>
					</h4>
					<Table striped bordered hover variant="dark" className="">
						<thead>
							<tr>
								<th>Rider</th>
								<th>Count</th>
							</tr>
						</thead>
						<tbody>
							{mostPickedRiders.length === 0 ? (
								<tr>
									<td colSpan={2} className="text-center">
										No data available.
									</td>
								</tr>
							) : (
								mostPickedRiders.map((mostPickedRider) => (
									<tr key={mostPickedRider.rider._id}>
										<td>
											{getCountryISO2(
												(mostPickedRider?.rider?.country ?? "")
													.toUpperCase()
													.trim(),
											) ? (
												<ReactCountryFlag
													countryCode={getCountryISO2(
														(mostPickedRider.rider.country ?? "")
															.toUpperCase()
															.trim(),
													)}
													svg
													style={{ width: "3.5rem" }}
												/>
											) : (
												mostPickedRider.rider.country
											)}
											{mostPickedRider.rider.first_name}{" "}
											<b>{mostPickedRider.rider.last_name}</b>
										</td>
										<td>{mostPickedRider.count}</td>
									</tr>
								))
							)}
						</tbody>
					</Table>
				</Card.Body>
			</Card>

			<Card className="my-3 shadow bg-secondary text-light border-0 mt-5">
				<Card.Body style={{ maxHeight: "400px", overflowY: "auto" }}>
					<h4 className="fw-bold ms-2">BEST COUNTRY</h4>
					<Table striped bordered hover variant="dark">
						<thead>
							<tr>
								<th>Country</th>
								<th>Points</th>
							</tr>
						</thead>
						<tbody>
							{bestCountry.length === 0 ? (
								<tr>
									<td colSpan={2} className="text-center">
										No data available.
									</td>
								</tr>
							) : (
								bestCountry.map((item) => (
									<tr key={item.country}>
										<td>
											<ReactCountryFlag
												countryCode={getCountryISO2(
													item.country.toUpperCase().trim(),
												)}
												svg
												style={{
													width: "2em",
													height: "2em",
													marginRight: "0.5em",
												}}
											/>
											{item.country}
										</td>
										<td>{item.count}</td>
									</tr>
								))
							)}
						</tbody>
					</Table>
				</Card.Body>
			</Card>
			<Card className="my-3 shadow bg-secondary text-light border-0 mt-5">
				<Card.Body style={{ maxHeight: "400px", overflowY: "auto" }}>
					<h4 className="fw-bold ms-2">MVP (Best Value Riders)</h4>
					<Table striped bordered hover variant="dark">
						<thead>
							<tr>
								<th>Rider</th>
								<th>Total Points</th>
								<th>Value</th>
								<th>Points/Rating Ratio</th>
							</tr>
						</thead>
						<tbody>
							{mvps.length === 0 ? (
								<tr>
									<td colSpan={4} className="text-center">
										No data available.
									</td>
								</tr>
							) : (
								mvps.map((mvp) => (
									<tr key={mvp.riderId}>
										<td>
											{mvp.rider_first_name} <b>{mvp.rider_last_name}</b>
										</td>
										<td>{mvp.total_points}</td>
										<td>${mvp.rider_rating}</td>
										<td>{mvp.ratio}</td>
									</tr>
								))
							)}
						</tbody>
					</Table>
				</Card.Body>
			</Card>
			</Container>
		</AdminLayout>
	);
};

export default AdminAnalyticsPage;
